// SegmentIterator.ts
// Provides forward iteration over segments with lazy loading and incremental buffering.
// Loads segments on-demand as iteration progresses to support year-long replays without freezing UI.

import { segmentIndexStore } from './SegmentIndexStore';
import { TickData, SegmentIndexEntry } from './SegmentSchema';

export interface IteratorConfig {
    symbol: string;
    startEpoch: number;
    endEpoch: number;
    granularity?: number;
    bufferSize?: number; // Number of ticks to buffer ahead (default 50K)
    batchSize?: number; // Number of ticks to fetch per batch (default 100)
    lazyLoad?: boolean; // Enable lazy loading (default true)
}

export interface IteratorState {
    currentIndex: number;
    totalTicks: number;
    currentEpoch: number | null;
    hasMore: boolean;
    segmentsLoaded: number;
    totalSegments: number;
    bufferSize: number;
}

/**
 * SegmentIterator: provides efficient lazy-loading forward iteration over segment data.
 * Features:
 * - Lazy loading: loads segments on-demand as iteration progresses (50K tick buffer default)
 * - Auto-refill: automatically loads next segments when buffer runs low
 * - nextAsync(): blocking method that waits for data to be available
 * - Memory efficient: keeps only active buffer + small lookahead in memory
 * - 1-year replay support: handles millions of ticks without UI freezes
 */
export class SegmentIterator {
    private _config: Required<IteratorConfig>;
    
    // Lazy loading state
    private _currentBuffer: TickData[] = [];
    private _currentIndex = 0;
    private _segmentQueue: SegmentIndexEntry[] = []; // Remaining segments to load
    private _loadedSegmentIds: Set<string> = new Set();
    private _loadedSegmentOrder: string[] = []; // Track load order for eviction
    private _isInitialized = false;
    private _isLoadingBuffer = false; // Guard against concurrent loads
    private _totalTicksInRange = 0; // Estimated total from segment metadata

    // Memory management
    private _maxLoadedSegments = 10; // Keep at most 10 segments in memory
    private _memoryPressureThreshold = 0.8; // 80% heap usage triggers eviction

    constructor(config: IteratorConfig) {
        this._config = {
            symbol: config.symbol,
            startEpoch: config.startEpoch,
            endEpoch: config.endEpoch,
            granularity: config.granularity ?? 0,
            bufferSize: config.bufferSize ?? 50000, // 50K ticks buffer (increased from 10K)
            batchSize: config.batchSize ?? 100,
            lazyLoad: config.lazyLoad ?? true,
        };
    }

    /**
     * Initialize iterator WITHOUT loading all data.
     * Queries segment metadata only and loads initial buffer.
     */
    async initialize(): Promise<void> {
        if (this._isInitialized) return;

        console.log('[SegmentIterator] Initializing LAZY LOAD for range:', {
            symbol: this._config.symbol,
            start: new Date(this._config.startEpoch),
            end: new Date(this._config.endEpoch),
            bufferSize: this._config.bufferSize,
        });

        // Query segment metadata only (not data)
        this._segmentQueue = segmentIndexStore.querySegments(
            this._config.symbol,
            this._config.startEpoch,
            this._config.endEpoch,
            this._config.granularity
        );

        // Estimate total ticks from segment metadata
        this._totalTicksInRange = this._segmentQueue.reduce((sum, entry) => sum + entry.count, 0);
        
        console.log('[SegmentIterator] Found', this._segmentQueue.length, 'segments with ~', 
            this._totalTicksInRange, 'ticks total');

        // If no segments found, this is critical - we need to understand why
        if (this._segmentQueue.length === 0) {
            console.error('[SegmentIterator] NO SEGMENTS FOUND! This means either:');
            console.error('  1. No data exists for the requested date range');
            console.error('  2. Symbol/granularity mismatch');
            console.error('  3. SegmentIndexStore not properly initialized');
            console.error('[SegmentIterator] Please check SegmentIndexStore logs above for details');
        }

        // Load initial buffer only
        await this._loadNextSegmentBatch();
        
        this._isInitialized = true;
        console.log('[SegmentIterator] Initialized with', this._currentBuffer.length, 'ticks in buffer');
    }

    /**
     * Load next batch of segments to fill buffer.
     * Loads segments until buffer reaches target size or all segments are loaded.
     */
    private async _loadNextSegmentBatch(): Promise<void> {
        if (this._isLoadingBuffer) {
            console.log('[SegmentIterator] Buffer load already in progress, skipping');
            return;
        }

        if (this._segmentQueue.length === 0) {
            console.log('[SegmentIterator] No more segments to load');
            return;
        }

        this._isLoadingBuffer = true;
        const batchStart = performance.now();
        
        try {
            const segmentsToLoad: SegmentIndexEntry[] = [];
            let estimatedTicks = 0;

            // Load segments until buffer is full or queue is empty
            while (
                estimatedTicks < this._config.bufferSize &&
                this._segmentQueue.length > 0
            ) {
                const entry = this._segmentQueue.shift()!;
                segmentsToLoad.push(entry);
                estimatedTicks += entry.count;
            }

            if (segmentsToLoad.length === 0) {
                this._isLoadingBuffer = false;
                return;
            }

            console.log('[SegmentIterator] Loading', segmentsToLoad.length, 'segments (~', 
                estimatedTicks, 'ticks)...');

            // Load segments in parallel
            const loadPromises = segmentsToLoad.map(entry =>
                segmentIndexStore.loadSegment(entry.segmentId)
            );
            const segments = await Promise.all(loadPromises);

            // Extract and decompress ticks
            const newTicks: TickData[] = [];
            let skippedSegments = 0;
            let ticksBeforeStart = 0;
            let ticksAfterEnd = 0;
            
            for (let i = 0; i < segments.length; i++) {
                const segment = segments[i];
                if (!segment) {
                    skippedSegments++;
                    console.warn('[SegmentIterator] Segment', segmentsToLoad[i].segmentId, 'returned null - data missing, skipping');
                    continue;
                }

                this._loadedSegmentIds.add(segmentsToLoad[i].segmentId);
                this._loadedSegmentOrder.push(segmentsToLoad[i].segmentId);

                // Decompress segment data (segments already have decompressed ticks)
                if ('ticks' in segment && Array.isArray((segment as any).ticks)) {
                    const ticks = (segment as any).ticks as TickData[];
                    if (ticks.length > 0) {
                        // Sort segment ticks to ensure chronological order
                        // Some segments may have been stored with unsorted data
                        ticks.sort((a, b) => a.epoch - b.epoch);
                        
                        // Filter ticks to only include those within the requested time range
                        // This is necessary because segments may overlap the range boundaries
                        for (const tick of ticks) {
                            if (tick.epoch < this._config.startEpoch) {
                                ticksBeforeStart++;
                                continue; // Skip ticks before start
                            }
                            if (tick.epoch > this._config.endEpoch) {
                                ticksAfterEnd++;
                                continue; // Skip ticks after end
                            }
                            newTicks.push(tick);
                        }
                    } else {
                        console.warn('[SegmentIterator] Segment', segmentsToLoad[i].segmentId, 'has no ticks - empty segment');
                    }
                } else {
                    console.warn('[SegmentIterator] Segment', segmentsToLoad[i].segmentId, 'has invalid format - skipping');
                }
            }
            
            if (ticksBeforeStart > 0 || ticksAfterEnd > 0) {
                console.log('[SegmentIterator] Filtered out', ticksBeforeStart, 'ticks before start and', ticksAfterEnd, 'ticks after end');
            }
            
            if (skippedSegments > 0) {
                console.log('[SegmentIterator] Skipped', skippedSegments, 'missing/invalid segments, loaded', newTicks.length, 'ticks from', segments.length - skippedSegments, 'valid segments');
            }

            // Evict old segments if exceeding limit
            this._evictIfNeeded();

            // Append to current buffer and always sort to maintain order
            // Even if segments are individually sorted, appending can create disorder
            // when new segment data interleaves with existing buffer data
            this._currentBuffer.push(...newTicks);

            // Always sort after adding new data to ensure correct order
            // This is critical for replay to avoid time-travel bugs
            if (this._currentBuffer.length > 0 && newTicks.length > 0) {
                const sortStart = performance.now();
                this._currentBuffer.sort((a, b) => a.epoch - b.epoch);
                const sortElapsed = performance.now() - sortStart;
                
                // Verify sort worked correctly
                let sortErrors = 0;
                for (let i = 1; i < this._currentBuffer.length; i++) {
                    if (this._currentBuffer[i].epoch < this._currentBuffer[i - 1].epoch) {
                        sortErrors++;
                        if (sortErrors <= 3) { // Log first 3 errors
                            console.error('[SegmentIterator] ❌ SORT FAILED at index', i, ':', 
                                new Date(this._currentBuffer[i - 1].epoch), '=>', new Date(this._currentBuffer[i].epoch));
                        }
                    }
                }
                if (sortErrors > 0) {
                    console.error('[SegmentIterator] ❌ Buffer has', sortErrors, 'unsorted pairs after sort!');
                }
                
                if (sortElapsed > 10 || sortErrors > 0) {
                    console.log('[SegmentIterator] Sorted buffer in', sortElapsed.toFixed(0), 'ms (', 
                        this._currentBuffer.length, 'ticks)', sortErrors > 0 ? '❌ ERRORS!' : '✓');
                }
            }

            const elapsed = performance.now() - batchStart;
            console.log('[SegmentIterator] Loaded batch in', elapsed.toFixed(0), 'ms, buffer now has',
                this._currentBuffer.length, 'ticks, segments remaining:', this._segmentQueue.length);

            // Additional eviction if memory pressure detected
            if (this._checkMemoryPressure()) {
                console.warn('[SegmentIterator] Memory pressure detected, evicting half of loaded segments');
                this._evictSegments(Math.ceil(this._loadedSegmentIds.size / 2));
            }

        } finally {
            this._isLoadingBuffer = false;
        }
    }

    /**
     * Get next tick in sequence.
     * Non-blocking: returns null if buffer exhausted, even if more segments available.
     * Use nextAsync() for blocking variant.
     */
    next(): TickData | null {
        if (!this._isInitialized) {
            console.warn('[SegmentIterator] Not initialized');
            return null;
        }

        // Check if buffer needs refill (async, don't block)
        if (this._currentIndex >= this._currentBuffer.length) {
            if (this._segmentQueue.length > 0) {
                // Trigger async load in background (don't await)
                this._loadNextSegmentBatch().catch(err =>
                    console.error('[SegmentIterator] Background buffer load failed:', err)
                );
            }
            return null; // End of current buffer
        }

        const tick = this._currentBuffer[this._currentIndex];
        this._currentIndex++;

        // Trigger preload when approaching buffer end (more aggressive: 50% threshold)
        const remainingInBuffer = this._currentBuffer.length - this._currentIndex;
        if (remainingInBuffer < this._config.bufferSize * 0.5 && this._segmentQueue.length > 0 && !this._isLoadingBuffer) {
            // Preload next batch (non-blocking)
            console.log('[SegmentIterator] Buffer at 50% capacity, preloading next segments...');
            this._loadNextSegmentBatch().catch(err =>
                console.error('[SegmentIterator] Preload failed:', err)
            );
        }

        return tick;
    }

    /**
     * Get next tick, blocking until data is available.
     * Async version that waits for buffer refill if needed.
     * Use for streaming playback when you need guaranteed data.
     */
    async nextAsync(): Promise<TickData | null> {
        if (!this._isInitialized) {
            console.warn('[SegmentIterator] Not initialized');
            return null;
        }

        // If buffer exhausted and more segments available, load them
        if (this._currentIndex >= this._currentBuffer.length && this._segmentQueue.length > 0) {
            await this._loadNextSegmentBatch();
        }

        // Return next tick if available
        if (this._currentIndex < this._currentBuffer.length) {
            const tick = this._currentBuffer[this._currentIndex];
            this._currentIndex++;
            
            // Trigger preload when approaching buffer end (50% threshold)
            const remainingInBuffer = this._currentBuffer.length - this._currentIndex;
            if (remainingInBuffer < this._config.bufferSize * 0.5 && this._segmentQueue.length > 0 && !this._isLoadingBuffer) {
                // Preload next batch (non-blocking, don't await)
                console.log('[SegmentIterator] ⚡ Buffer at 50% capacity (nextAsync), preloading next segments...');
                console.log('[SegmentIterator] Buffer state:', {
                    remaining: remainingInBuffer,
                    total: this._currentBuffer.length,
                    percentRemaining: ((remainingInBuffer / this._currentBuffer.length) * 100).toFixed(1) + '%',
                    segmentsRemaining: this._segmentQueue.length
                });
                this._loadNextSegmentBatch().catch(err =>
                    console.error('[SegmentIterator] Preload failed:', err)
                );
            }
            
            return tick;
        }

        // No more data
        return null;
    }

    /**
     * Get next batch of ticks, blocking until data is available.
     * Async version for batch operations.
     */
    async nextBatchAsync(size?: number): Promise<TickData[]> {
        const batchSize = size ?? this._config.batchSize;
        const batch: TickData[] = [];

        for (let i = 0; i < batchSize; i++) {
            const tick = await this.nextAsync();
            if (!tick) break;
            batch.push(tick);
        }

        return batch;
    }

    /**
     * Peek at next tick without advancing.
     */
    peek(): TickData | null {
        if (!this._isInitialized || this._currentIndex >= this._currentBuffer.length) {
            return null;
        }
        return this._currentBuffer[this._currentIndex];
    }

    /**
     * Get batch of ticks (for fast-forward).
     * Non-blocking: returns available ticks up to batchSize, may be less than requested.
     * Use nextBatchAsync() for guaranteed batch size.
     */
    nextBatch(size?: number): TickData[] {
        const batchSize = size ?? this._config.batchSize;
        const batch: TickData[] = [];

        for (let i = 0; i < batchSize && this._currentIndex < this._currentBuffer.length; i++) {
            batch.push(this._currentBuffer[this._currentIndex]);
            this._currentIndex++;

            // Trigger preload when batch ends (more aggressive: 50% threshold)
            const remainingInBuffer = this._currentBuffer.length - this._currentIndex;
            if (remainingInBuffer < this._config.bufferSize * 0.5 && this._segmentQueue.length > 0 && !this._isLoadingBuffer) {
                console.log('[SegmentIterator] Buffer at 50% in batch, preloading...');
                this._loadNextSegmentBatch().catch(err =>
                    console.error('[SegmentIterator] Preload during batch failed:', err)
                );
            }
        }

        return batch;
    }

    /**
     * Seek to specific epoch.
     * Binary search on current buffer only.
     * Warning: seeking outside buffer requires loading new segments.
     */
    seekTo(targetEpoch: number): number {
        if (!this._isInitialized) {
            console.warn('[SegmentIterator] Not initialized');
            return -1;
        }

        // Binary search for target epoch in current buffer
        let left = 0;
        let right = this._currentBuffer.length - 1;
        let result = -1;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            if (this._currentBuffer[mid].epoch >= targetEpoch) {
                result = mid;
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        }

        if (result !== -1) {
            this._currentIndex = result;
            console.log('[SegmentIterator] Seeked to index:', result, 'epoch:', 
                new Date(this._currentBuffer[result].epoch));
        } else {
            console.warn('[SegmentIterator] Target epoch not in buffer:', new Date(targetEpoch));
        }

        return result;
    }

    /**
     * Reset iterator to beginning.
     * Warning: does NOT reload segments. Use initialize() for fresh start.
     */
    reset(): void {
        this._currentIndex = 0;
        console.log('[SegmentIterator] Reset to beginning');
    }

    /**
     * Get iterator state.
     */
    getState(): IteratorState {
        const current = this._currentIndex < this._currentBuffer.length 
            ? this._currentBuffer[this._currentIndex] 
            : null;
        
        return {
            currentIndex: this._currentIndex,
            totalTicks: this._totalTicksInRange,
            currentEpoch: current?.epoch ?? null,
            hasMore: this._currentIndex < this._currentBuffer.length || this._segmentQueue.length > 0,
            segmentsLoaded: this._loadedSegmentIds.size,
            totalSegments: this._loadedSegmentIds.size + this._segmentQueue.length,
            bufferSize: this._currentBuffer.length,
        };
    }

    /**
     * Evict loaded segments when exceeding limits.
     * Best-effort: trims consumed buffer first, then unloads oldest segments.
     */
    private _evictIfNeeded(): void {
        if (this._loadedSegmentIds.size <= this._maxLoadedSegments) return;

        const overLimit = this._loadedSegmentIds.size - this._maxLoadedSegments;
        console.warn('[SegmentIterator] Loaded segments exceed limit, evicting', overLimit);
        this._evictSegments(overLimit);
    }

    /**
     * Evict a specific number of oldest segments (FIFO based on load order).
     */
    private _evictSegments(count: number): void {
        if (count <= 0) return;

        // Drop consumed ticks to reclaim memory before unloading segments.
        if (this._currentIndex > 0) {
            this._currentBuffer = this._currentBuffer.slice(this._currentIndex);
            this._currentIndex = 0;
        }

        for (let i = 0; i < count && this._loadedSegmentOrder.length > 0; i++) {
            const segmentId = this._loadedSegmentOrder.shift()!;
            this._loadedSegmentIds.delete(segmentId);
            segmentIndexStore.unloadSegment(segmentId);
        }

        console.log('[SegmentIterator] Eviction complete. Loaded segments:', this._loadedSegmentIds.size);
    }

    /**
     * Check JS heap usage to decide if we should evict more aggressively.
     */
    private _checkMemoryPressure(): boolean {
        const memoryInfo = (performance as any)?.memory;
        if (!memoryInfo || !memoryInfo.jsHeapSizeLimit) return false;

        const ratio = memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit;
        return ratio >= this._memoryPressureThreshold;
    }

    /**
     * Get remaining tick count (estimated).
     */
    getRemainingCount(): number {
        return Math.max(0, this._totalTicksInRange - this._currentIndex);
    }

    /**
     * Check if iterator has more ticks.
     * May return true even if current buffer is exhausted (segments to load).
     */
    hasNext(): boolean {
        return this._currentIndex < this._currentBuffer.length || this._segmentQueue.length > 0;
    }

    /**
     * Check if more segments can be loaded.
     */
    hasMoreSegments(): boolean {
        return this._segmentQueue.length > 0;
    }

    /**
     * Get all remaining ticks in current buffer (does NOT load new segments).
     */
    peekRemaining(): TickData[] {
        return this._currentBuffer.slice(this._currentIndex);
    }

    /**
     * Get range of ticks around current position in buffer.
     */
    getWindow(beforeCount: number, afterCount: number): TickData[] {
        const start = Math.max(0, this._currentIndex - beforeCount);
        const end = Math.min(this._currentBuffer.length, this._currentIndex + afterCount);
        return this._currentBuffer.slice(start, end);
    }

    /**
     * Get current buffer size.
     */
    getBufferSize(): number {
        return this._currentBuffer.length;
    }

    /**
     * Get count of loaded segments.
     */
    getLoadedSegmentCount(): number {
        return this._loadedSegmentIds.size;
    }

    /**
     * Trim old ticks from buffer to free memory.
     * Keeps only last N ticks and adjusts current index.
     */
    trimBuffer(keepSize: number = 10000): void {
        if (this._currentBuffer.length <= keepSize) return;

        const removeCount = this._currentBuffer.length - keepSize;
        console.log('[SegmentIterator] Trimming buffer: removing', removeCount, 'old ticks, keeping', keepSize);

        this._currentBuffer.splice(0, removeCount);
        this._currentIndex = Math.max(0, this._currentIndex - removeCount);
    }

    /**
     * Destroy iterator and free resources.
     */
    destroy(): void {
        this._currentBuffer = [];
        this._currentIndex = 0;
        this._segmentQueue = [];
        this._loadedSegmentIds.clear();
        this._isInitialized = false;
        console.log('[SegmentIterator] Destroyed');
    }
}

/**
 * Create iterator from configuration.
 */
export async function createSegmentIterator(config: IteratorConfig): Promise<SegmentIterator> {
    const iterator = new SegmentIterator(config);
    await iterator.initialize();
    return iterator;
}
